import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import useScrollMemory from 'hooks/useScrollMemory';
import { DesignSystemToken, TokenCategory } from 'designSystem/types';
import { groupTokensByTheme } from 'designSystem/content/util';
import TokenGrid from '../../tokens/TokenGrid';
import FilterPills from '../../FilterPills';
import * as Base from '../../styles';

// Map TokenCategory to display name
const categoryDisplayNames: Record<TokenCategory, string> = {
	color: 'Colors',
	typography: 'Typography',
	spacing: 'Spacing',
	elevation: 'Elevation',
	borderRadius: 'Border Radius',
	opacity: 'Opacity',
	motion: 'Motion',
	other: 'Other',
};

const AllTokensView = () => {
	const { designSystemContent, selectedCategories, activeTab } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			selectedCategories: r.mode.designSystemFilters.theme.selectedCategories,
			activeTab: selectActiveDsTab(r.mode),
		}),
		shallowEqual
	);

	const modes = designSystemContent?.config?.display?.modes ?? [];
	const modeIds = modes.map((m) => m.id);
	const defaultModeId = modes.find((m) => m.default)?.id ?? modes[0]?.id;
	const scrollRef = useScrollMemory(activeTab?.id ?? null);

	// Get available categories with counts
	const availableCategories = useMemo(() => {
		if (!designSystemContent?.theme) return [];
		return (Object.keys(categoryDisplayNames) as TokenCategory[]).map((category) => ({
			value: category,
			label: categoryDisplayNames[category],
			count: designSystemContent.theme!.byCategory[category]?.length || 0,
		}));
	}, [designSystemContent?.theme]);

	// Filter tokens based on selected categories and search query
	const tokensToDisplay = useMemo(() => {
		if (!designSystemContent?.theme) return [];

		let tokens: DesignSystemToken[] = [];

		// If categories selected, show union of all selected categories
		if (selectedCategories.length > 0) {
			const tokenSets = selectedCategories.map(
				(cat) => designSystemContent.theme!.byCategory[cat] || []
			);
			// Union: combine all sets and remove duplicates
			const tokenMap = new Map<string, DesignSystemToken>();
			tokenSets.forEach((tokenSet) => {
				tokenSet.forEach((token) => {
					tokenMap.set(token.id, token);
				});
			});
			tokens = Array.from(tokenMap.values());
		} else {
			// No categories selected, show all
			tokens = designSystemContent.theme.all || [];
		}

		return tokens;
	}, [designSystemContent?.theme, selectedCategories]);

	const groupedByTheme = useMemo(() => {
		return groupTokensByTheme(tokensToDisplay, modeIds);
	}, [tokensToDisplay, modeIds]);

	const nonDefaultModeIds = modeIds.filter((id) => id !== defaultModeId);

	return (
		<Base.SectionBase ref={scrollRef}>
			<Base.SectionHeader>
				<Base.SectionTitle>Theme</Base.SectionTitle>
			</Base.SectionHeader>
			<Base.SectionContent>
				<FilterPills section="theme" categories={availableCategories} />
				{tokensToDisplay.length === 0 ? (
					<p>
						No theme tokens found
						{selectedCategories.length > 0 && ` matching selected categories`}.
					</p>
				) : (
					<>
						{groupedByTheme.base.length > 0 && (
							<TokenGrid tokens={groupedByTheme.base} />
						)}
						{nonDefaultModeIds.map((modeId) => {
							const tokens = groupedByTheme[modeId] ?? [];
							if (tokens.length === 0) return null;
							const modeLabel = modes.find((m) => m.id === modeId)?.label ?? modeId;
							return (
								<div key={modeId}>
									<Base.SubSectionTitle>{modeLabel} Overrides</Base.SubSectionTitle>
									<TokenGrid tokens={tokens} />
								</div>
							);
						})}
					</>
				)}
			</Base.SectionContent>
		</Base.SectionBase>
	);
};

export default AllTokensView;
